import { useCallback, useEffect, useState } from "react";
import { fetchAuditorStatistics, type AuditorStatisticsResponse } from "../api/auditorApi";
import { getAuthToken, getStoredAccountType } from "../auth";
import {
  statisticsDashboardSummary,
  statisticsKeyIndicators,
  statisticsMonthlyInvoiced,
  statisticsMonthlyTrend,
} from "../data/statisticsDashboard";

export function useAuditorStatistics() {
  const [data, setData] = useState<AuditorStatisticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken() || getStoredAccountType() !== "auditor") {
      setUsingApi(false);
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const response = await fetchAuditorStatistics();
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les statistiques.");
      setData(null);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = data
    ? {
        admissions: data.summary.admissions,
        invoicedUsd: data.summary.invoiced_usd,
        companies: data.summary.companies,
        hospitals: data.summary.hospitals,
        year: data.year,
      }
    : statisticsDashboardSummary;
  const indicators = data
    ? [
        { ...statisticsKeyIndicators[0], value: data.indicators.admissions },
        { ...statisticsKeyIndicators[1], value: data.indicators.invoiced_usd },
        { ...statisticsKeyIndicators[2], value: data.indicators.avg_invoice_usd },
        { ...statisticsKeyIndicators[3], value: data.indicators.companies },
        { ...statisticsKeyIndicators[4], value: data.indicators.hospitals },
        { ...statisticsKeyIndicators[5], value: data.indicators.employees },
      ]
    : statisticsKeyIndicators;

  const monthlyTrend = data?.monthly_admissions ?? statisticsMonthlyTrend;
  const monthlyInvoiced = data?.monthly_invoiced ?? statisticsMonthlyInvoiced;
  const year = data?.year ?? statisticsDashboardSummary.year;

  return {
    summary,
    indicators,
    monthlyTrend,
    monthlyInvoiced,
    year,
    loading,
    error,
    usingApi,
    reload: load,
  };
}
